import React, { useEffect } from 'react';
import { FormControlLabel, Checkbox, Box, FormControl, FormLabel, RadioGroup, Radio, Stack } from '@mui/material';
import Loading from './Loading';
import LoadableComponent from '../../generic/LoadableComponent';
import usePersistentStore, { getLayersState, useLayersBucket } from '../../../store/persistent/persistentStore';
import LayerStandard from '../../../../Simulations/LayerStandard';
import { getGrid } from '../../../../store/LoadingStore';
import Grid from '../../../../Geodata/grid';
import Layer from '../../../../Simulations/Layer';
import { LineColor, RaysLoadingStatus } from '@visualizer/types';
import CustomTooltip from '../../generic/CustomTooltip';

type IntersectionsProps = {
    activeSensorId: number;
    isCurrentSensorValid: boolean;
    loadIntersections: boolean;
    setLoadIntersections: (value: boolean) => void;
    loadRays: boolean;
};

const Intersections: React.FC<IntersectionsProps> = (props) => {
    const [loadingStatus, setLoadingStatus] = React.useState<RaysLoadingStatus>(RaysLoadingStatus.NotLoaded);
    const [loadingErrorMessage, setLoadingErrorMessage] = React.useState('');
    const updateLayerState = usePersistentStore((s) => s.updateCurrentLayerState);
    const activeLayerState = usePersistentStore((s) => s.getActiveLayerState());
    const simulationItems = activeLayerState?.simulationItems;
    const { showInts = false, closestInts = false, otherInts = false, whichIntersections = 0 } = simulationItems ?? {};
    const activeLayerId = useLayersBucket((s) => s.activeLayerID);

    // Cache the active layer id locally and react to external changes
    const [cachedActiveLayerId, setCachedActiveLayerId] = React.useState<string>(() => getLayersState().activeLayerID);

    //
    /// Cleanup on unmount
    //
    useEffect(() => {
        return () => {
            const layer = getGrid().searchLayerByUID(cachedActiveLayerId);
            if (layer instanceof LayerStandard) {
                layer.disposeIntersections();
                layer.unloadIntersections(setLoadingStatus);
            }
        };
    }, []);

    const renderMainControls = () => {
        return (
            <Stack spacing={1} sx={{ px: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        columnGap: 0.8,
                        rowGap: 0.25,
                        '& .MuiFormControlLabel-root': { margin: 0, minHeight: 26, padding: 0 },
                        '& .MuiCheckbox-root': { padding: 0.25 },
                        '& .MuiFormControlLabel-label': { lineHeight: 1.1, whiteSpace: 'nowrap' },
                    }}
                >
                    <FormControlLabel
                        control={<Checkbox size="small" checked={showInts} onChange={(e) => handleChange('showInts', e.target.checked)} />}
                        label="Visible"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox size="small" checked={closestInts} onChange={(e) => handleChange('closestInts', e.target.checked)} />
                        }
                        label="Closest"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox size="small" checked={otherInts} onChange={(e) => handleChange('otherInts', e.target.checked)} />
                        }
                        label="Others"
                    />
                </Box>

                <Box sx={{ flex: 1 }}>
                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">Sensor Point</FormLabel>
                        <RadioGroup
                            row
                            className="panel-compact-control-stack"
                            sx={{ gap: 1.2, mt: 0.25 }}
                            value={whichIntersections}
                            onChange={(_, v) => handleChange('whichIntersections', Number(v))}
                        >
                            <FormControlLabel value={0} control={<Radio />} label="All" />
                            <FormControlLabel value={1} control={<Radio />} label="Selected" />
                            <CustomTooltip side="left" title="Coming soon!">
                                <FormControlLabel disabled value={2} control={<Radio />} label="Current Line" />
                            </CustomTooltip>
                        </RadioGroup>
                    </FormControl>
                </Box>
            </Stack>
        );
    };

    const handleChange = (key: string, value: boolean | number | number[] | LineColor) => {
        if (!simulationItems) return;

        updateLayerState({
            simulationItems: {
                ...simulationItems,
                [key]: value,
            },
        });
    };

    useEffect(() => {
        const oldLayer: Layer = getGrid().searchLayerByUID(cachedActiveLayerId);
        if (oldLayer instanceof LayerStandard) oldLayer.disposeIntersections();

        if (oldLayer instanceof LayerStandard) oldLayer.unloadIntersections(setLoadingStatus);
        setCachedActiveLayerId(activeLayerId);
    }, [activeLayerId]);

    useEffect(() => {
        const activeLayer: LayerStandard = getGrid().getActiveLayer() as LayerStandard;
        if (!(activeLayer instanceof LayerStandard)) return;

        if (props.loadIntersections) {
            activeLayer.loadItems(setLoadingStatus, 'ints', setLoadingErrorMessage);
        } else {
            activeLayer.unloadIntersections(setLoadingStatus);
            setLoadingErrorMessage('');
            if (showInts) {
                handleChange('showInts', false);
            }
        }
    }, [props.loadIntersections, showInts]);

    useEffect(() => {
        const activeLayer: LayerStandard = getGrid().getActiveLayer() as LayerStandard;
        if (!(activeLayer instanceof LayerStandard)) return;

        if (showInts) {
            const sensorIds = (() => {
                switch (whichIntersections) {
                    case 0:
                        return Grid.sensorPoints.map((sp) => sp.ID);
                    case 1:
                    case 2:
                        return [props.activeSensorId];
                    default:
                        throw new Error('Invalid kind of sensor data value');
                }
            })();
            activeLayer.updateIntsMesh(showInts, sensorIds);
        } else {
            activeLayer.disposeIntersections();
        }
    }, [showInts, closestInts, otherInts, whichIntersections, props.activeSensorId]);

    const isLoadingIntersections = loadingStatus === RaysLoadingStatus.Initializing || loadingStatus === RaysLoadingStatus.Loading;
    const statusForContent = loadingStatus === RaysLoadingStatus.Loading ? RaysLoadingStatus.NotLoaded : loadingStatus;
    const intersectionsTooltip = 'Coming soon!'; // !props.loadRays ? getSentence('Visualization.Intersection.disable.Tooltip') : '';

    return (
        <Box>
            <LoadableComponent loading={isLoadingIntersections} indicatorPlacement="end" disableWhileLoading={false}>
                <CustomTooltip side="left" mode="hover" title={intersectionsTooltip}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            ml: 0,
                            my: 0.5,
                            columnGap: 0.6,
                            rowGap: 0,
                            '& .MuiFormControlLabel-root': { margin: 0, minHeight: 26, padding: 0 },
                            '& .MuiCheckbox-root': { padding: 0.25 },
                            '& .MuiFormControlLabel-label': { lineHeight: 1.1, whiteSpace: 'nowrap' },
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    disabled={true} // {!props.loadRays}
                                    name="loadIntersections"
                                    checked={props.loadIntersections}
                                    onChange={(e) => props.setLoadIntersections(e.target.checked)}
                                />
                            }
                            label="Intersections"
                        />
                    </Box>
                </CustomTooltip>
            </LoadableComponent>

            <Box>
                <Loading status={statusForContent} errorMessage={loadingErrorMessage}>
                    {props.isCurrentSensorValid && renderMainControls()}
                </Loading>
            </Box>
        </Box>
    );
};

export default Intersections;
